import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { API_BASE } from '../config';
import { api, errorText } from '../lib/api';
import { useToast } from '../contexts/ToastContext';
import AccountSettings from '../components/AccountSettings';
import WatchlistSettings from '../components/WatchlistSettings';
import { SkeletonList } from '../components/ui/Skeleton';
import ErrorState from '../components/ui/ErrorState';
import { useUnsavedChangesWarning } from '../lib/useUnsavedChangesWarning';
import {
  Settings,
  User,
  Briefcase,
  MapPin,
  DollarSign,
  Save,
  RefreshCw,
  CheckCircle,
  Plus,
  X,
  Bell,
  Send
} from 'lucide-react';

interface UserProfile {
  full_name: string;
  email: string;
  phone: string;
  location: string;
  linkedin_url: string;
  github_url: string;
  portfolio_url: string;
  current_title: string;
  target_titles: string[];
  skills: string[];
  experience_years: number;
  seniority_level: string;
  preferred_locations: string[];
  remote_preference: string;
  salary_min: number;
  salary_max: number;
  industries: string[];
  job_types: string[];
  summary: string;
}

const defaultProfile: UserProfile = {
  full_name: '', email: '', phone: '', location: '',
  linkedin_url: '', github_url: '', portfolio_url: '',
  current_title: '', target_titles: [], skills: [],
  experience_years: 0, seniority_level: 'mid',
  preferred_locations: [], remote_preference: 'hybrid',
  salary_min: 0, salary_max: 0, industries: [], job_types: [],
  summary: '',
};

interface TagInputProps {
  label: string;
  tags: string[];
  inputValue: string;
  setInputValue: (v: string) => void;
  placeholder: string;
  onAdd: () => void;
  onRemove: (value: string) => void;
}

// Defined at module scope (NOT nested inside SettingsPage) so its component
// identity is stable across renders. A component declared inside another
// component is a brand-new function on every render, so React unmounts and
// remounts it on each keystroke — which made these inputs lose focus after
// every character typed ("bouncy" fields that appeared not to work).
const TagInput: React.FC<TagInputProps> = ({
  label, tags, inputValue, setInputValue, placeholder, onAdd, onRemove,
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">{label}</label>
    <div className="flex flex-wrap gap-1.5 mb-2">
      {tags.map((tag) => (
        <span key={tag} className="inline-flex items-center px-2.5 py-0.5 text-xs bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400 rounded-full">
          {tag}
          <button onClick={() => onRemove(tag)} className="ml-1 hover:text-primary-900" aria-label={`Remove ${tag}`}>
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
    </div>
    <div className="flex space-x-2">
      <input
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAdd(); } }}
        className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm dark:bg-slate-800 dark:text-white"
        placeholder={placeholder}
        aria-label={label}
      />
      <button
        onClick={onAdd}
        className="px-3 py-2 bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700"
        aria-label={`Add ${label}`}
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  </div>
);

const SettingsPage: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile>(defaultProfile);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pristine, setPristine] = useState('');
  const [notifStatus, setNotifStatus] = useState<{ resend_configured: boolean; email_configured: boolean; ready: boolean } | null>(null);
  const [sendingTest, setSendingTest] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [sendingAlert, setSendingAlert] = useState(false);
  const { toast } = useToast();

  // Tag input states
  const [newSkill, setNewSkill] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [newIndustry, setNewIndustry] = useState('');

  const dirty = JSON.stringify(profile) !== pristine;
  useUnsavedChangesWarning(dirty && !loading);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await api.get<Partial<UserProfile>>('/api/profile', { retry: 2 });
      const next: UserProfile = {
        full_name: data.full_name || '',
        email: data.email || '',
        phone: data.phone || '',
        location: data.location || '',
        linkedin_url: data.linkedin_url || '',
        github_url: data.github_url || '',
        portfolio_url: data.portfolio_url || '',
        current_title: data.current_title || '',
        target_titles: data.target_titles || [],
        skills: data.skills || [],
        experience_years: data.experience_years || 0,
        seniority_level: data.seniority_level || 'mid',
        preferred_locations: data.preferred_locations || [],
        remote_preference: data.remote_preference || 'hybrid',
        salary_min: data.salary_min || 0,
        salary_max: data.salary_max || 0,
        industries: data.industries || [],
        job_types: data.job_types || [],
        summary: data.summary || '',
      };
      setProfile(next);
      setPristine(JSON.stringify(next));
    } catch (e) {
      setLoadError(errorText(e, 'Failed to load your profile'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  useEffect(() => {
    fetch(`${API_BASE}/api/notifications/status`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setNotifStatus(d))
      .catch(() => {});
  }, []);

  const sendNotification = async (type: 'test' | 'digest' | 'match-alert') => {
    const setters: Record<string, (v: boolean) => void> = {
      test: setSendingTest, digest: setSendingDigest, 'match-alert': setSendingAlert,
    };
    setters[type](true);
    try {
      const data = await api.post<{ message?: string }>(`/api/notifications/${type}`);
      toast.success(data.message || 'Sent');
    } catch (e) {
      toast.error(errorText(e, `Failed to send ${type}`));
    } finally {
      setters[type](false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.put('/api/profile', profile);
      setPristine(JSON.stringify(profile));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      toast.error(errorText(e, 'Failed to save profile'));
    } finally {
      setSaving(false);
    }
  };

  const addTag = (field: keyof UserProfile, value: string, setter: (v: string) => void) => {
    if (!value.trim()) return;
    const current = profile[field] as string[];
    if (!current.includes(value.trim())) {
      setProfile({ ...profile, [field]: [...current, value.trim()] });
    }
    setter('');
  };

  const removeTag = (field: keyof UserProfile, value: string) => {
    const current = profile[field] as string[];
    setProfile({ ...profile, [field]: current.filter((v) => v !== value) });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <SkeletonList rows={6} />
      </div>
    );
  }
  if (loadError) {
    return <ErrorState message={loadError} onRetry={fetchProfile} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Settings className="w-8 h-8 text-primary-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Settings</h1>
            <p className="text-gray-600 dark:text-slate-400 mt-1">Manage your professional profile and job search preferences</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? (
            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving...</>
          ) : saved ? (
            <><CheckCircle className="w-4 h-4 mr-2" />Saved!</>
          ) : (
            <><Save className="w-4 h-4 mr-2" />Save Profile</>
          )}
        </button>
      </div>

      {/* Personal Info */}
      <motion.div
        className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="p-6 border-b border-gray-200 dark:border-slate-800">
          <div className="flex items-center space-x-2">
            <User className="w-5 h-5 text-gray-600 dark:text-slate-400" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Personal Information</h3>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Full Name</label>
              <input
                type="text"
                value={profile.full_name}
                onChange={(e) => setProfile({ ...profile, full_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-slate-800 dark:text-white"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Email</label>
              <input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-slate-800 dark:text-white"
                placeholder="john@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Phone</label>
              <input
                type="tel"
                value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-slate-800 dark:text-white"
                placeholder="+1 (555) 123-4567"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Location</label>
              <input
                type="text"
                value={profile.location}
                onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-slate-800 dark:text-white"
                placeholder="San Francisco, CA"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">LinkedIn URL</label>
              <input
                type="url"
                value={profile.linkedin_url}
                onChange={(e) => setProfile({ ...profile, linkedin_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-slate-800 dark:text-white"
                placeholder="https://linkedin.com/in/..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">GitHub URL</label>
              <input
                type="url"
                value={profile.github_url}
                onChange={(e) => setProfile({ ...profile, github_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-slate-800 dark:text-white"
                placeholder="https://github.com/..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Portfolio URL</label>
              <input
                type="url"
                value={profile.portfolio_url}
                onChange={(e) => setProfile({ ...profile, portfolio_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-slate-800 dark:text-white"
                placeholder="https://..."
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Professional Summary</label>
            <textarea
              value={profile.summary}
              onChange={(e) => setProfile({ ...profile, summary: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none dark:bg-slate-800 dark:text-white"
              placeholder="Brief professional summary..."
            />
          </div>
        </div>
      </motion.div>

      {/* Career Preferences */}
      <motion.div
        className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="p-6 border-b border-gray-200 dark:border-slate-800">
          <div className="flex items-center space-x-2">
            <Briefcase className="w-5 h-5 text-gray-600 dark:text-slate-400" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Career Preferences</h3>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Current Title</label>
              <input
                type="text"
                value={profile.current_title}
                onChange={(e) => setProfile({ ...profile, current_title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-slate-800 dark:text-white"
                placeholder="Software Engineer"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Experience (years)</label>
              <input
                type="number"
                value={profile.experience_years}
                onChange={(e) => setProfile({ ...profile, experience_years: parseInt(e.target.value, 10) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-slate-800 dark:text-white"
                min="0"
                max="50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Seniority Level</label>
              <select
                value={profile.seniority_level}
                onChange={(e) => setProfile({ ...profile, seniority_level: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-slate-800 dark:text-white"
              >
                <option value="junior">Junior</option>
                <option value="mid">Mid-level</option>
                <option value="senior">Senior</option>
                <option value="staff">Staff</option>
                <option value="principal">Principal</option>
                <option value="director">Director</option>
                <option value="vp">VP</option>
                <option value="executive">Executive</option>
              </select>
            </div>
          </div>

          <TagInput
            label="Target Job Titles"
            tags={profile.target_titles}
            inputValue={newTitle}
            setInputValue={setNewTitle}
            placeholder="Add a target title and press Enter"
            onAdd={() => addTag('target_titles', newTitle, setNewTitle)}
            onRemove={(tag) => removeTag('target_titles', tag)}
          />

          <TagInput
            label="Skills"
            tags={profile.skills}
            inputValue={newSkill}
            setInputValue={setNewSkill}
            placeholder="Add a skill and press Enter"
            onAdd={() => addTag('skills', newSkill, setNewSkill)}
            onRemove={(tag) => removeTag('skills', tag)}
          />

          <TagInput
            label="Industries"
            tags={profile.industries}
            inputValue={newIndustry}
            setInputValue={setNewIndustry}
            placeholder="Add an industry and press Enter"
            onAdd={() => addTag('industries', newIndustry, setNewIndustry)}
            onRemove={(tag) => removeTag('industries', tag)}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Remote Preference</label>
              <select
                value={profile.remote_preference}
                onChange={(e) => setProfile({ ...profile, remote_preference: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-slate-800 dark:text-white"
              >
                <option value="remote">Remote Only</option>
                <option value="hybrid">Hybrid</option>
                <option value="onsite">On-site</option>
                <option value="any">Any</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Job Types</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {['Full-time', 'Part-time', 'Contract', 'Internship'].map((type) => (
                  <label key={type} className="inline-flex items-center">
                    <input
                      type="checkbox"
                      checked={profile.job_types.includes(type)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setProfile({ ...profile, job_types: [...profile.job_types, type] });
                        } else {
                          setProfile({ ...profile, job_types: profile.job_types.filter((t) => t !== type) });
                        }
                      }}
                      className="rounded border-gray-300 dark:border-slate-700 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-1.5 text-sm text-gray-700 dark:text-slate-300">{type}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Location & Compensation */}
      <motion.div
        className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="p-6 border-b border-gray-200 dark:border-slate-800">
          <div className="flex items-center space-x-2">
            <MapPin className="w-5 h-5 text-gray-600 dark:text-slate-400" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Location & Compensation</h3>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <TagInput
            label="Preferred Locations"
            tags={profile.preferred_locations}
            inputValue={newLocation}
            setInputValue={setNewLocation}
            placeholder="Add a location and press Enter"
            onAdd={() => addTag('preferred_locations', newLocation, setNewLocation)}
            onRemove={(tag) => removeTag('preferred_locations', tag)}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                <DollarSign className="w-3.5 h-3.5 inline" /> Minimum Salary
              </label>
              <input
                type="number"
                value={profile.salary_min || ''}
                onChange={(e) => setProfile({ ...profile, salary_min: parseInt(e.target.value, 10) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-slate-800 dark:text-white"
                placeholder="e.g. 120000"
                step="5000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                <DollarSign className="w-3.5 h-3.5 inline" /> Maximum Salary
              </label>
              <input
                type="number"
                value={profile.salary_max || ''}
                onChange={(e) => setProfile({ ...profile, salary_max: parseInt(e.target.value, 10) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-slate-800 dark:text-white"
                placeholder="e.g. 180000"
                step="5000"
              />
            </div>
          </div>
        </div>
      </motion.div>

      {/* Notifications */}
      <motion.div
        className="bg-white dark:bg-navy-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="p-6 border-b border-gray-200 dark:border-slate-800">
          <div className="flex items-center space-x-2">
            <Bell className="w-5 h-5 text-gray-600 dark:text-slate-400" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Email Notifications</h3>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {/* Status indicators */}
          <div className="flex flex-wrap gap-3">
            <span className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-full ${notifStatus?.resend_configured ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
              {notifStatus?.resend_configured ? 'Resend API configured' : 'Resend API not configured'}
            </span>
            <span className={`inline-flex items-center px-3 py-1 text-xs font-medium rounded-full ${notifStatus?.email_configured ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'}`}>
              {notifStatus?.email_configured ? 'Email address set' : 'Set your email above'}
            </span>
          </div>

          {!notifStatus?.resend_configured && (
            <p className="text-sm text-gray-500 dark:text-slate-400">
              To enable email notifications, add your Resend API key via{' '}
              <code className="text-xs bg-gray-100 dark:bg-slate-800 px-1 py-0.5 rounded">wrangler secret put RESEND_API_KEY</code>.
              Free tier provides 100 emails/day.
            </p>
          )}

          <p className="text-sm text-gray-600 dark:text-slate-400">
            Notifications are sent automatically after scheduled scrapes when new jobs are found. You can also trigger them manually below.
          </p>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => sendNotification('test')}
              disabled={sendingTest || !notifStatus?.ready}
              className="flex items-center px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700 disabled:opacity-50 text-sm"
            >
              <Send className="w-3.5 h-3.5 mr-2" />
              {sendingTest ? 'Sending...' : 'Send Test Email'}
            </button>
            <button
              onClick={() => sendNotification('match-alert')}
              disabled={sendingAlert || !notifStatus?.ready}
              className="flex items-center px-4 py-2 bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/40 disabled:opacity-50 text-sm"
            >
              <Bell className="w-3.5 h-3.5 mr-2" />
              {sendingAlert ? 'Sending...' : 'Send Match Alert'}
            </button>
            <button
              onClick={() => sendNotification('digest')}
              disabled={sendingDigest || !notifStatus?.ready}
              className="flex items-center px-4 py-2 bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/40 disabled:opacity-50 text-sm"
            >
              <Send className="w-3.5 h-3.5 mr-2" />
              {sendingDigest ? 'Sending...' : 'Send Daily Digest'}
            </button>
          </div>
        </div>
      </motion.div>

      {/* Companies to watch — auto-crawled daily */}
      <WatchlistSettings />

      {/* Account — password change + delete account */}
      <AccountSettings />

      {/* Bottom Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? (
            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Saving...</>
          ) : saved ? (
            <><CheckCircle className="w-4 h-4 mr-2" />Saved!</>
          ) : (
            <><Save className="w-4 h-4 mr-2" />Save All Changes</>
          )}
        </button>
      </div>
    </div>
  );
};

export default SettingsPage;
